import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, Landmark, Banknote, CreditCard, Smartphone, Check, Loader2, Zap } from "lucide-react";
import "./AddReceivingAccountOverlay.css";

const TYPES = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "bankTransfer", label: "Bank Transfer", icon: Landmark },
  { value: "POS", label: "POS Terminal", icon: CreditCard },
  { value: "mobilePay", label: "Mobile Pay", icon: Smartphone, disabled: true },
];

const BANKS = [
  "Access Bank",
  "GTBank",
  "Zenith Bank",
  "First Bank",
  "UBA",
  "Moniepoint MFB",
  "Opay",
  "PalmPay",
  "Kuda",
  "Wema Bank",
  "Fidelity Bank",
  "Union Bank",
  "Sterling Bank",
  "Stanbic IBTC",
  "Ecobank",
  "Keystone Bank",
  "Polaris Bank",
  "Providus Bank",
  "Rubies MFB",
  "Sparkle Bank",
  "VFD Microfinance Bank",
  "Other",
];

// isPartner: true = StoreLense has a live API integration with this provider.
// Selecting a partner provider asks for a sell/auth token so the integration
// can be wired up (mapped to schema's isPartner.sellToken / hasIntgration).
// To onboard a new partner later, just flip its isPartner flag to true below.
const POS_PROVIDERS = [
  { value: "Moniepoint", isPartner: false },
  { value: "Opay", isPartner: false },
  { value: "Kuda", isPartner: true },
  { value: "PalmPay", isPartner: false },
  { value: "Paga", isPartner: false },
  { value: "Interswitch", isPartner: false },
  { value: "Baxi", isPartner: false },
  { value: "StorelensePOS", isPartner: false },
  { value: "Other", isPartner: false },
];

function emptyFormForType(type) {
  return {
    paymentType: type,
    name: "",
    provider: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    terminalId: "",
    merchantId: "",
    walletNumber: "",
    merchantCode: "",
    sellToken: "",
  };
}

function getPosProviderMeta(providerValue) {
  return POS_PROVIDERS.find((p) => p.value === providerValue) || null;
}

function isValid(form) {
  if (!form.name.trim()) return false;
  switch (form?.paymentType) {
    case "bankTransfer":
      return Boolean(form?.bankName && form?.accountNumber?.trim() && form?.accountName?.trim());
    case "POS": {
      if (!form?.provider || !form?.terminalId?.trim()) return false;
      const providerMeta = getPosProviderMeta(form.provider);
      if (providerMeta?.isPartner && !form?.sellToken?.trim()) return false;
      return true;
    }
    case "mobilePay":
      return Boolean(form?.provider && form?.walletNumber?.trim());
    case "cash":
    default:
      return true;
  }
}

// Builds the API payload, nesting the isPartner sub-document per schema
// when a partner POS provider is selected.
function buildPayload(form) {
  const { sellToken, ...rest } = form;
  if (form.paymentType !== "POS") return rest;
  console.log(rest);

  const providerMeta = getPosProviderMeta(form.provider);
  return {
    ...rest,
    serialNumber: rest.terminalId,
    isPartner: {
      hasIntgration: providerMeta?.isPartner ?? false,
      sellToken: providerMeta?.isPartner ? sellToken.trim() : null,
      postype: 1,
    },
  };
}

export default function AddReceivingAccountOverlay({ initialAccount, onClose, onSave }) {
  const isEdit = Boolean(initialAccount);
  const [step, setStep] = useState(isEdit ? "details" : "type");
  const [form, setForm] = useState(
    isEdit
      ? {
          ...emptyFormForType(initialAccount.paymentType),
          ...initialAccount,
          sellToken: initialAccount.isPartner?.sellToken || "",
        }
      : emptyFormForType(""),
  );
  const [saving, setSaving] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const selectType = (type) => {
    if (TYPES.find((t) => t.value === type)?.disabled) return;
    setForm(emptyFormForType(type));
    setStep("details");
  };

  const handleSave = async () => {
    if (!isValid(form)) return;
    setSaving(true);
    try {
      await onSave?.(buildPayload(form));
    } finally {
      setSaving(false);
    }
  };

  const activeTypeMeta = TYPES.find((t) => t.value === form.paymentType);
  const posProviderMeta = form.paymentType === "POS" ? getPosProviderMeta(form.provider) : null;

  return createPortal(
    <div className='araoc-backdrop' onMouseDown={onClose}>
      <div className='araoc-panel' onMouseDown={(e) => e.stopPropagation()}>
        <div className='araoc-panel__header'>
          {step === "details" && !isEdit ? (
            <button type='button' className='araoc-panel__back' onClick={() => setStep("type")} aria-label='Back'>
              <ChevronLeft size={18} strokeWidth={2.25} />
            </button>
          ) : (
            <span className='araoc-panel__header-spacer' />
          )}
          <h3 className='araoc-panel__title'>{isEdit ? "Edit Payment Account" : "Add Payment Account"}</h3>
          <button type='button' className='araoc-panel__close' onClick={onClose} aria-label='Close'>
            <X size={18} strokeWidth={2.25} />
          </button>
        </div>

        <div className='araoc-panel__body'>
          {step === "type" && (
            <div className='araoc-type-grid'>
              {TYPES.map(({ value, label, icon: Icon, disabled }) => (
                <button
                  key={value}
                  type='button'
                  className={`araoc-type-card${disabled ? " araoc-type-card--disabled" : ""}`}
                  onClick={() => selectType(value)}
                  disabled={disabled}
                  aria-disabled={disabled}
                >
                  <span className='araoc-type-card__icon'>
                    <Icon size={20} strokeWidth={2.25} />
                  </span>
                  <span className='araoc-type-card__label'>{label}</span>
                  {disabled && <span className='araoc-type-card__soon'>Coming soon</span>}
                </button>
              ))}
            </div>
          )}

          {step === "details" && (
            <div className='araoc-form'>
              {isEdit && activeTypeMeta && (
                <div className='araoc-form__type-pill'>
                  <activeTypeMeta.icon size={14} strokeWidth={2.25} />
                  {activeTypeMeta.label}
                </div>
              )}

              <label className='araoc-field'>
                <span className='araoc-field__label'>Name</span>
                <input
                  type='text'
                  className='araoc-field__input'
                  placeholder={form.paymentType === "cash" ? "Cash Drawer" : "Account name"}
                  value={form.name}
                  onChange={update("name")}
                  autoFocus
                />
              </label>

              {form.paymentType === "cash" && (
                <label className='araoc-field'>
                  <span className='araoc-field__label'>Description (optional)</span>
                  <input type='text' className='araoc-field__input' placeholder='Main Counter' value={form.provider} onChange={update("provider")} />
                </label>
              )}

              {form.paymentType === "bankTransfer" && (
                <>
                  <label className='araoc-field'>
                    <span className='araoc-field__label'>Bank</span>
                    <select className='araoc-field__input' value={form.bankName} onChange={update("bankName")}>
                      <option value=''>Select bank</option>
                      {BANKS.map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className='araoc-field'>
                    <span className='araoc-field__label'>Account Name</span>
                    <input type='text' className='araoc-field__input' placeholder='Storelense Ltd' value={form.accountName} onChange={update("accountName")} />
                  </label>
                  <label className='araoc-field'>
                    <span className='araoc-field__label'>Account Number</span>
                    <input
                      type='text'
                      inputMode='numeric'
                      className='araoc-field__input'
                      placeholder='0123456789'
                      value={form.accountNumber}
                      onChange={update("accountNumber")}
                    />
                  </label>
                </>
              )}

              {form.paymentType === "POS" && (
                <>
                  <label className='araoc-field'>
                    <span className='araoc-field__label'>Provider</span>
                    <select className='araoc-field__input' value={form.provider} onChange={update("provider")}>
                      <option value=''>Select provider</option>
                      {POS_PROVIDERS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.value}
                          {p.isPartner ? " (Partner)" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className='araoc-field'>
                    <span className='araoc-field__label'>Terminal Serial No</span>
                    <input type='text' className='araoc-field__input' placeholder='MP392822' value={form.terminalId} onChange={update("terminalId")} />
                  </label>
                  <label className='araoc-field'>
                    <span className='araoc-field__label'>Merchant ID</span>
                    <input type='text' className='araoc-field__input' placeholder='Optional' value={form.merchantId} onChange={update("merchantId")} />
                  </label>

                  {posProviderMeta?.isPartner && (
                    <>
                      <div className='araoc-form__partner-pill'>
                        <Zap size={13} strokeWidth={2.5} />
                        Partner integration — connects live via API
                      </div>
                      <label className='araoc-field'>
                        <span className='araoc-field__label'>Sell Token</span>
                        <input
                          type='text'
                          className='araoc-field__input'
                          placeholder="Paste the provider's sell/auth token"
                          value={form.sellToken}
                          onChange={update("sellToken")}
                        />
                      </label>
                    </>
                  )}
                </>
              )}

              <button type='button' className='araoc-save-btn' onClick={handleSave} disabled={!isValid(form) || saving}>
                {saving ? (
                  <Loader2 size={16} strokeWidth={2.5} className='araoc-spin' />
                ) : (
                  <>
                    <Check size={16} strokeWidth={2.5} />
                    Save
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
